import './cinematic-spatial-surface.js';
import * as THREE from "three";
import { initV6xJourney } from './v6x-journey.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { interpret, POSITION_LABELS, POSITION_COLORS } from './interpreter.js';
import { audioEngine } from './audio-engine.js';

const canvas = document.querySelector("#world");
const depthEl = document.querySelector("#depth");
const seasonName = document.querySelector("#season-name");
const progress = document.querySelector(".rail-progress");
const seasons = [...document.querySelectorAll(".season")];

if (!canvas) {
  throw new Error("V6-CINEMATIC-01: #world canvas not found.");
}

/* WebGPU capability detection — full renderer migration is an architectural pass,
   requires Three.js WebGPURenderer node material system rebuild */
navigator.gpu?.requestAdapter?.().then(a => {
  if (a) {
    document.documentElement.dataset.gpuTier = 'webgpu-capable';
    console.log('[CL] WebGPU adapter available.');
  }
}).catch(() => {});

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  alpha: true,
  powerPreference: "high-performance",
  preserveDrawingBuffer: true
});

const _isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || window.innerWidth < 768;
renderer.setPixelRatio(_isMobile ? Math.min(window.devicePixelRatio, 1.5) : Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.9;

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x060a14);
scene.fog = new THREE.FogExp2(0x060a14, 0.011);


const camera = new THREE.PerspectiveCamera(
  43,
  window.innerWidth / window.innerHeight,
  0.1,
  160
);

camera.position.set(0, 0.15, 10.5);

/* ── POST-PROCESSING ─────────────────────────────────────────────── */
const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));

// GTAO (ground truth ambient occlusion) — Lumen-style contact shadows
// Skip on mobile: too expensive on constrained GPUs
if (!_isMobile) {
  const gtaoPass = new GTAOPass(scene, camera, window.innerWidth, window.innerHeight);
  gtaoPass.output = GTAOPass.OUTPUT.Default;
  gtaoPass.blendIntensity = 1.0;
  const ao = gtaoPass.gtaoMaterial?.uniforms;
  if (ao) {
    // tighten radius to keep AO tight against geometry, not the whole sphere
    if (ao["radius"]) ao["radius"].value = 0.5;
    if (ao["distanceExponent"]) ao["distanceExponent"].value = 2.0;
    if (ao["thickness"]) ao["thickness"].value = 1.0;
    if (ao["scale"]) ao["scale"].value = 1.0;
    if (ao["samples"]) ao["samples"].value = 16;
  }
  composer.addPass(gtaoPass);
}

const bloomPass = new UnrealBloomPass(
  new THREE.Vector2(window.innerWidth, window.innerHeight),
  0.28,   // strength
  0.35,   // radius
  0.30    // threshold — glow fires below this luminance
);
composer.addPass(bloomPass);
composer.addPass(new OutputPass());

const world = new THREE.Group();
scene.add(world);

/* ------------------------------------------------------------------
   PERSISTENT ENVIRONMENT
   The environment exists continuously across every narrative season.
   ------------------------------------------------------------------ */

const environment = new THREE.Group();
world.add(environment);

const coreSystem = new THREE.Group();
environment.add(coreSystem);

const coreGeometry = new THREE.IcosahedronGeometry(2.75, 5);

/* ── ANIMATED DISPLACEMENT TEXTURE ──────────────────────────────── */
/* Multi-octave plasma sine field drives organic vertex displacement */
const dispCanvas = document.createElement('canvas');
dispCanvas.width = 128; dispCanvas.height = 128;
const dispCtx = dispCanvas.getContext('2d');
const dispTexture = new THREE.CanvasTexture(dispCanvas);

function updateDispTexture(time, pulse) {
  const img = dispCtx.createImageData(128, 128);
  const d = img.data;
  const t = time * 0.00028;
  const p = 0.6 + pulse * 2.0;
  for (let y = 0; y < 128; y++) {
    const ny = y / 127;
    for (let x = 0; x < 128; x++) {
      const nx = x / 127;
      let v = Math.sin(nx * 8.2 + t * 1.1) * Math.sin(ny * 6.7 + t * 0.8);
      v += Math.sin((nx + ny) * 9.4 + t * 1.4) * 0.52;
      v += Math.sin(Math.sqrt((nx - 0.5) ** 2 + (ny - 0.5) ** 2) * 22 + t * 1.7) * 0.34;
      const val = Math.min(255, Math.max(0, Math.floor((v * p + 2.5) / 5 * 255)));
      const i = (y * 128 + x) * 4;
      d[i] = d[i+1] = d[i+2] = val;
      d[i+3] = 255;
    }
  }
  dispCtx.putImageData(img, 0, 0);
  dispTexture.needsUpdate = true;
}

const coreMaterial = new THREE.MeshStandardMaterial({
  color: 0x3060b0,
  emissive: 0x0d2860,
  emissiveIntensity: 0.55,
  roughness: 0.18,
  metalness: 0.78,
  transparent: true,
  opacity: 0.88,
  displacementMap: dispTexture,
  displacementScale: 0.38,
  displacementBias: -0.14
});

const core = new THREE.Mesh(coreGeometry, coreMaterial);
coreSystem.add(core);

const shellGeometry = new THREE.IcosahedronGeometry(3.15, 4);

const shellMaterial = new THREE.MeshBasicMaterial({
  color: 0xb8d4ff,
  wireframe: true,
  transparent: true,
  opacity: 0.32
});

const shell = new THREE.Mesh(shellGeometry, shellMaterial);
coreSystem.add(shell);

const innerGeometry = new THREE.IcosahedronGeometry(2.25, 3);

const innerMaterial = new THREE.MeshBasicMaterial({
  color: 0xd4e8ff,
  wireframe: true,
  transparent: true,
  opacity: 0.22
});

const innerShell = new THREE.Mesh(innerGeometry, innerMaterial);
coreSystem.add(innerShell);


/* ── CORE GLOW HALO ─────────────────────────────────────────────── */
/* Soft additive sprite at center simulates bloom without post-processing */
const glowCanvas = document.createElement("canvas");
glowCanvas.width = 256; glowCanvas.height = 256;
const glowCtx = glowCanvas.getContext("2d");
const grad = glowCtx.createRadialGradient(128, 128, 0, 128, 128, 128);
grad.addColorStop(0, "rgba(80,120,210,0.38)");
grad.addColorStop(0.25, "rgba(50,90,180,0.18)");
grad.addColorStop(0.6, "rgba(20,50,120,0.06)");
grad.addColorStop(1, "rgba(0,0,0,0)");
glowCtx.fillStyle = grad;
glowCtx.fillRect(0, 0, 256, 256);
const glowTex = new THREE.CanvasTexture(glowCanvas);
const glowMat = new THREE.SpriteMaterial({
  map: glowTex,
  transparent: true,
  blending: THREE.AdditiveBlending,
  depthWrite: false,
  opacity: 0.62
});
const glowSprite = new THREE.Sprite(glowMat);
glowSprite.scale.set(8.5, 8.5, 1);
coreSystem.add(glowSprite);

/* ------------------------------------------------------------------
   ORBITAL STRUCTURE
   Subtle spatial references prevent the object from reading as a
   standalone decorative 3D asset.
   ------------------------------------------------------------------ */

const orbital = new THREE.Group();
environment.add(orbital);

function createOrbit(radius, rotationX, rotationZ, opacity) {
  const geometry = new THREE.RingGeometry(
    radius - 0.006,
    radius + 0.006,
    160
  );

  const material = new THREE.MeshBasicMaterial({
    color: 0x9db8e4,
    transparent: true,
    opacity,
    side: THREE.DoubleSide
  });

  const ring = new THREE.Mesh(geometry, material);

  ring.rotation.x = rotationX;
  ring.rotation.z = rotationZ;

  orbital.add(ring);
  return ring;
}

const orbitA = createOrbit(4.4, Math.PI * 0.48, 0.18, 0.22);
const orbitB = createOrbit(5.9, Math.PI * 0.36, -0.42, 0.14);
const orbitC = createOrbit(7.8, Math.PI * 0.58, 0.7, 0.08);

/* ------------------------------------------------------------------
   DEEP FIELD
   Three spatial layers create atmospheric depth rather than a flat
   particle background.
   ------------------------------------------------------------------ */

function createDust(count, radiusMin, radiusMax, vertical, size, opacity) {
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(count * 3);

  for (let i = 0; i < count; i++) {
    const radius =
      radiusMin + Math.random() * (radiusMax - radiusMin);

    const angle = Math.random() * Math.PI * 2;
    const y = (Math.random() - 0.5) * vertical;

    positions[i * 3] = Math.cos(angle) * radius;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = Math.sin(angle) * radius;
  }

  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3)
  );

  const material = new THREE.PointsMaterial({
    color: 0x91a9d2,
    size,
    transparent: true,
    opacity,
    depthWrite: false
  });

  const field = new THREE.Points(geometry, material);
  environment.add(field);

  return field;
}

const nearDust = createDust(700, 7, 18, 13, 0.026, 0.30);
const midDust = createDust(900, 18, 38, 25, 0.018, 0.18);
const farDust = createDust(1100, 36, 72, 45, 0.012, 0.10);

/* ------------------------------------------------------------------
   LIGHT LANGUAGE
   A restrained three-point environment keeps the material coherent.
   ------------------------------------------------------------------ */

const ambient = new THREE.AmbientLight(0x7182a0, 1.8);
scene.add(ambient);

// Sky/ground hemisphere for warm-cool bounce — gives scene dimensional depth
const hemiLight = new THREE.HemisphereLight(0x8899cc, 0x1a1220, 0.6);
scene.add(hemiLight);

const keyLight = new THREE.PointLight(0xaec8ff, 72, 55);
keyLight.position.set(5.5, 4.5, 8);
scene.add(keyLight);

const rimLight = new THREE.PointLight(0x6080cc, 42, 45);
rimLight.position.set(-6, -2, -4);
scene.add(rimLight);

const topLight = new THREE.PointLight(0xc8d8ff, 22, 34);
topLight.position.set(0, 8, 1);
scene.add(topLight);

const fillLight = new THREE.PointLight(0x3355aa, 28, 40);
fillLight.position.set(0, -5, 6);
scene.add(fillLight);

/* ------------------------------------------------------------------
   CAMERA LANGUAGE
   Camera movement is slow, continuous, and subordinate to the
   narrative. Pointer input creates parallax rather than steering.
   ------------------------------------------------------------------ */

const pointer = { x: 0, y: 0 };

/* ── SPHERE + TEXT HOVER — igloo.inc reactive surface ───────────── */
/* Fires when cursor crosses the sphere mesh OR any headline text.   */
const raycaster = new THREE.Raycaster();
let sphereHoverBoost = 0;
let _sphereWasHovered = false;
let _textHovered = false;


const cameraState = {
  x: 0,
  y: 0,
  z: 10.5,
  lookX: 0,
  lookY: 0,
  lookZ: 0
};

let currentScroll = 0;
let activeSeason = 0;

/* ── 3D NARRATIVE CAMERA PATH ────────────────────────────────────── */
/* Six keyframes — one per season. Camera arcs through 3D space as   */
/* the user scrolls, making the sphere a story object not a backdrop. */
const CAM_PATH = [
  { x:  0.00, y:  0.15, z: 10.50, lx:  0.00, ly:  0.00 }, // arrival
  { x: -1.40, y:  0.90, z:  9.40, lx: -0.25, ly:  0.12 }, // recognition
  { x:  1.10, y: -0.60, z: 11.80, lx:  0.15, ly: -0.10 }, // disruption
  { x: -0.60, y:  1.50, z:  8.20, lx: -0.08, ly:  0.20 }, // contraction
  { x:  2.20, y:  0.40, z:  8.80, lx:  0.30, ly:  0.05 }, // emergence
  { x:  0.00, y:  0.20, z: 10.20, lx:  0.00, ly:  0.00 }, // question
];

function camPathAt(t) {
  const n = CAM_PATH.length - 1;
  const clamped = Math.max(0, Math.min(1, t));
  const seg = Math.min(n - 1, Math.floor(clamped * n));
  const f = clamped * n - seg;
  const smooth = f * f * (3 - 2 * f); // smoothstep within segment
  const a = CAM_PATH[seg], b = CAM_PATH[seg + 1];
  return {
    x:  a.x  + (b.x  - a.x)  * smooth,
    y:  a.y  + (b.y  - a.y)  * smooth,
    z:  a.z  + (b.z  - a.z)  * smooth,
    lx: a.lx + (b.lx - a.lx) * smooth,
    ly: a.ly + (b.ly - a.ly) * smooth,
  };
}

/* Native scroll tracking — feeds the 3D camera path without intercepting
   the page's own scroll behavior (Lenis conflicted with p47 overlay) */
window.addEventListener('scroll', () => {
  currentScroll = window.scrollY;
  updateSeason();
}, { passive: true });

window.addEventListener("pointermove", event => {
  const clientX = event.clientX ?? (event.touches?.[0]?.clientX ?? 0);
  const clientY = event.clientY ?? (event.touches?.[0]?.clientY ?? 0);
  pointer.x = clientX / window.innerWidth * 2 - 1;
  pointer.y = clientY / window.innerHeight * 2 - 1;
  audioEngine.pan(clientX, clientY);
  const _under = document.elementFromPoint(clientX, clientY);
  _textHovered = !!(_under?.closest('h1, h2, .p47-13-title, .p47-13-copy'));
});

/* Touch support — map touch to pointer for the sphere hover raycasting */
window.addEventListener("touchmove", event => {
  const t = event.touches[0];
  if (!t) return;
  pointer.x = t.clientX / window.innerWidth * 2 - 1;
  pointer.y = t.clientY / window.innerHeight * 2 - 1;
}, { passive: true });

function updateSeason() {
  if (!seasons.length) return;

  const index = Math.min(
    seasons.length - 1,
    Math.floor(
      (currentScroll + window.innerHeight * 0.5) /
      window.innerHeight
    )
  );

  if (index === activeSeason) return;

  activeSeason = index;

  seasons.forEach((season, i) => {
    season.classList.toggle("active", i === activeSeason);
  });

  if (seasonName) {
    seasonName.textContent =
      seasons[activeSeason]?.dataset.season || "ARRIVAL";
  }

  if (progress) {
    progress.style.height =
      `${((activeSeason + 1) / seasons.length) * 100}%`;
  }

  audioEngine.season(activeSeason);
  navigator.vibrate?.([6]);
}

function updateCamera(time) {
  const totalH = Math.max(1, document.body.scrollHeight - window.innerHeight);
  const scrollT = Math.min(1, currentScroll / totalH);
  const wp = camPathAt(scrollT);

  const targetX = wp.x + pointer.x * 0.28 + Math.sin(time * 0.00013) * 0.11;
  const targetY = wp.y - pointer.y * 0.16 + Math.cos(time * 0.00011) * 0.07;
  const targetZ = wp.z + Math.sin(time * 0.00009) * 0.08;

  cameraState.x += (targetX - cameraState.x) * 0.018;
  cameraState.y += (targetY - cameraState.y) * 0.018;
  cameraState.z += (targetZ - cameraState.z) * 0.012;

  camera.position.set(cameraState.x, cameraState.y, cameraState.z);

  const lookTargetX = wp.lx + pointer.x * 0.12 + Math.sin(time * 0.00016) * 0.035;
  const lookTargetY = wp.ly - pointer.y * 0.08 + Math.cos(time * 0.00014) * 0.025;

  cameraState.lookX += (lookTargetX - cameraState.lookX) * 0.02;
  cameraState.lookY += (lookTargetY - cameraState.lookY) * 0.02;

  camera.lookAt(cameraState.lookX, cameraState.lookY, cameraState.lookZ);
}

function updateEnvironment(time, phase) {
  const hv = sphereHoverBoost;
  core.rotation.x += 0.00065 + phase * 0.00055 + hv * 0.0045;
  core.rotation.y += 0.00105 + phase * 0.00075 + hv * 0.0065;

  shell.rotation.x -= 0.00036;
  shell.rotation.y += 0.00058;

  innerShell.rotation.x += 0.00082;
  innerShell.rotation.y -= 0.00046;

  const breathing =
    1 +
    Math.sin(time * 0.00042) * 0.018 +
    phase * 0.055;

  coreSystem.scale.setScalar(breathing);

  orbital.rotation.y += 0.00022 + phase * 0.00012;
  orbital.rotation.x =
    Math.sin(time * 0.00008) * 0.035;

  nearDust.rotation.y += 0.00018;
  nearDust.rotation.x += 0.000025;

  midDust.rotation.y -= 0.000075;
  midDust.rotation.x += 0.000018;

  farDust.rotation.y += 0.000032;

  keyLight.position.x =
    5.5 + Math.sin(time * 0.00018) * 1.8;

  keyLight.position.y =
    4.5 + Math.cos(time * 0.00015) * 1.1;

  rimLight.position.x =
    -6 + Math.cos(time * 0.00012) * 1.5;

  const materialPulse =
    0.62 +
    Math.sin(time * 0.0005) * 0.042 +
    phase * 0.055;

  questionBoost += ((questionActive ? 1 : 0) - questionBoost) * 0.022;

  const combinedBoost = questionBoost + sphereHoverBoost * 0.6;

  coreMaterial.opacity = Math.min(0.98, materialPulse + combinedBoost * 0.22);
  coreMaterial.emissiveIntensity = (0.55 + Math.sin(time * 0.00042) * 0.12 + combinedBoost * 0.22) * (1 + sphereHoverBoost * 0.4);
  coreMaterial.displacementScale = 0.38 + sphereHoverBoost * 0.35;
  shellMaterial.opacity = Math.min(0.40, 0.18 + combinedBoost * 0.12 + sphereHoverBoost * 0.14);
  glowMat.opacity = (0.22 + Math.sin(time * 0.00038) * 0.06 + combinedBoost * 0.10) * (1 + sphereHoverBoost * 0.4);
  glowSprite.scale.setScalar(7.0 + sphereHoverBoost * 2.5);
  keyLight.intensity = 32 + combinedBoost * 28 + sphereHoverBoost * 22;
  rimLight.intensity = 18 + combinedBoost * 14 + sphereHoverBoost * 12;
  topLight.intensity = 10 + combinedBoost * 10 + sphereHoverBoost * 8;

  updateDispTexture(time, combinedBoost);

}

function animate(time) {
  requestAnimationFrame(animate);

  const denominator = Math.max(seasons.length - 1, 1);
  const phase = activeSeason / denominator;

  /* Sphere hover — raycasting at NDC pointer coords */
  raycaster.setFromCamera(new THREE.Vector2(pointer.x, -pointer.y), camera);
  const _hits = raycaster.intersectObject(core, false);
  const _hovered = _hits.length > 0 || _textHovered;
  sphereHoverBoost += ((_hovered ? 1 : 0) - sphereHoverBoost) * 0.055;
  const _prevHovered = _sphereWasHovered;
  if (_hovered && !_prevHovered) {
    document.body.classList.add('sphere-hover');
  } else if (!_hovered && _prevHovered) {
    document.body.classList.remove('sphere-hover');
  }
  _sphereWasHovered = _hovered;

  /* igloo-active fires for BOTH sphere hit and text hover */
  document.body.classList.toggle('igloo-active', sphereHoverBoost > 0.05);

  updateEnvironment(time, phase);
  updateCamera(time);

  if (depthEl) {
    depthEl.textContent = String(
      Math.round(currentScroll / Math.max(window.innerHeight, 1) * 100)
    ).padStart(3, "0");
  }

  composer.render();
}

window.addEventListener("resize", () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();

  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  composer.setSize(window.innerWidth, window.innerHeight);
  bloomPass.setSize(window.innerWidth, window.innerHeight);
});

updateSeason();

/* ------------------------------------------------------------------
   QUESTION SECTION BOOST
   Tracked here; applied inside updateEnvironment below.
   ------------------------------------------------------------------ */
let questionBoost = 0;
let questionActive = false;

const questionSection = document.querySelector(".narrative-question");
if (questionSection) {
  new IntersectionObserver(entries => {
    questionActive = entries[0].isIntersecting;
  }, { threshold: 0.25 }).observe(questionSection);
}

animate(0);

/* ── HOUDINI PAINT WORKLET ───────────────────────────────────────── */
if ('paintWorklet' in CSS) {
  CSS.paintWorklet.addModule('/noise-worklet.js').catch(() => {});
}

/* ── SECTION REVEAL ANIMATIONS ───────────────────────────────────── */
const revealObserver = new IntersectionObserver(entries => {
  entries.forEach(el => {
    if (el.isIntersecting) {
      el.target.classList.add('cl-revealed');
      revealObserver.unobserve(el.target);
    }
  });
}, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

document.querySelectorAll(
  '.p47-13-title, .p47-13-callout, .p47-13-kicker, .p47-13-panel'
).forEach(el => {
  el.classList.add('cl-reveal');
  revealObserver.observe(el);
});

// History cards: scroll + rAF loop because IntersectionObserver
// doesn't fire reliably when parent transitions from display:none
document.querySelectorAll('.p47-13-history-card').forEach(card => {
  card.classList.add('cl-reveal');
});
let _historyRevealed = false;
function _tryRevealHistory() {
  if (_historyRevealed) return;
  const hist = document.querySelector('#p47-13-history');
  if (!hist) return;
  const cs = window.getComputedStyle(hist);
  if (cs.display === 'none' || cs.visibility === 'hidden') {
    requestAnimationFrame(_tryRevealHistory);
    return;
  }
  const rect = hist.getBoundingClientRect();
  if (rect.top < window.innerHeight * 1.15 && rect.bottom > 0) {
    _historyRevealed = true;
    document.querySelectorAll('.p47-13-history-card').forEach((card, i) => {
      card.classList.remove('cl-reveal');
      card.style.animationDelay = `${i * 90}ms`;
      card.classList.add('cl-revealed');
    });
  } else {
    requestAnimationFrame(_tryRevealHistory);
  }
}
window.addEventListener('scroll', _tryRevealHistory, { passive: true });
requestAnimationFrame(_tryRevealHistory);

/* ── AUDIO START — first interaction ─────────────────────────────── */
const startAudio = () => {
  audioEngine.start();
  document.removeEventListener('pointerdown', startAudio);
  document.removeEventListener('keydown', startAudio);
};
document.addEventListener('pointerdown', startAudio, { once: true });
document.addEventListener('keydown', startAudio, { once: true });

/* ── CURSOR GLOW ─────────────────────────────────────────────────── */
const cursorGlow = document.createElement('div');
cursorGlow.className = 'cl-cursor-glow';
document.body.appendChild(cursorGlow);

let cursorX = 0, cursorY = 0;
let cursorTargetX = 0, cursorTargetY = 0;

document.addEventListener('pointermove', e => {
  cursorTargetX = e.clientX;
  cursorTargetY = e.clientY;
});

(function cursorLoop() {
  cursorX += (cursorTargetX - cursorX) * 0.12;
  cursorY += (cursorTargetY - cursorY) * 0.12;
  cursorGlow.style.transform = `translate(${cursorX - 120}px, ${cursorY - 120}px)`;
  requestAnimationFrame(cursorLoop);
})();

document.querySelector("#begin")?.addEventListener(
  "click",
  () => {
    audioEngine.start();
    audioEngine.pulse(2);
    navigator.vibrate?.([12, 40, 28]);
    window.dispatchEvent(
      new CustomEvent("contrarian:intake")
    );
  }
);

/* ── DEVICE ORIENTATION — gyroscope sphere parallax ─────────────── */
/* On mobile: phone tilt drives sphere rotation and camera parallax  */
const orient = { beta: 0, gamma: 0, active: false };

function handleOrientation(e) {
  if (e.beta === null) return;
  orient.active = true;
  orient.beta  += ((e.beta  ?? 0) - orient.beta)  * 0.08;
  orient.gamma += ((e.gamma ?? 0) - orient.gamma) * 0.08;
  pointer.x = (orient.gamma / 45) * 0.8;
  pointer.y = ((orient.beta - 45) / 45) * 0.5;
  audioEngine.pan(
    (orient.gamma / 90 + 0.5) * window.innerWidth,
    (orient.beta  / 90)       * window.innerHeight
  );
}

if (typeof DeviceOrientationEvent !== 'undefined') {
  if (typeof DeviceOrientationEvent.requestPermission === 'function') {
    document.addEventListener('pointerdown', function grantGyro() {
      DeviceOrientationEvent.requestPermission()
        .then(s => { if (s === 'granted') window.addEventListener('deviceorientation', handleOrientation); })
        .catch(() => {});
      document.removeEventListener('pointerdown', grantGyro);
    }, { once: true });
  } else {
    window.addEventListener('deviceorientation', handleOrientation, { passive: true });
  }
}



/* ============================================================
   V6_NARRATIVE_CONTROLLER
   Presentation/orchestration only.
   Semantic interpretation remains exclusively authoritative V6.
   ============================================================ */

const V6_NARRATIVE_CONTROLLER = (() => {
  const questions = [
    {
      id: "role",
      label: "What is your current role?",
      prompt: "Describe the work you are actually responsible for.",
      type: "text"
    },
    {
      id: "industry",
      label: "What industry are you operating in?",
      prompt: "Name the industry or sector where your work actually happens.",
      type: "text"
    },
    {
      id: "experience",
      label: "How much experience have you accumulated?",
      prompt: "Consider the experience that informs how you make decisions.",
      type: "text"
    },
    {
      id: "reliance",
      label: "What do people rely on you for?",
      prompt: "Identify the contribution others consistently expect from you.",
      type: "text"
    },
    {
      id: "strengths",
      label: "What are you unusually good at?",
      prompt: "Describe the capabilities you believe differentiate your work.",
      type: "text"
    },
    {
      id: "achievement",
      label: "What achievement best represents your value?",
      prompt: "Choose an outcome rather than a credential.",
      type: "text"
    },
    {
      id: "uncertainty",
      label: "Where is uncertainty highest?",
      prompt: "Identify what has become less predictable in your environment.",
      type: "text"
    },
    {
      id: "success",
      label: "What does success currently mean to you?",
      prompt: "Define the outcome you are actually trying to create.",
      type: "text"
    },
    {
      id: "failure",
      label: "What would failure look like?",
      prompt: "Name the consequence you most need to avoid.",
      type: "text"
    },
    {
      id: "ai-capability",
      label: "What can AI increasingly do in your work?",
      prompt: "Be concrete about what is becoming reproducible.",
      type: "text"
    },
    {
      id: "human-advantage",
      label: "What remains uniquely human in your contribution?",
      prompt: "Identify the judgment, context, relationship, or agency that remains scarce.",
      type: "text"
    },
    {
      id: "desired",
      label: "Where do you want your position to move?",
      prompt: "Describe what you want to be known for and paid to do — the work and influence you want to move toward.",
      type: "text"
    }
  ]

  const answers = {}
  let index = 0

  const stage = document.querySelector("#v6-question-stage")
  const form = document.querySelector("#v6-questionnaire")
  const examination = document.querySelector("#v6-examination")
  const engine = document.querySelector("#v6-engine")
  const output = document.querySelector("#v6-output")
  const outputContent = document.querySelector("#v6-output-content")
  const final = document.querySelector("#final-interpretation")
  const finalContent = document.querySelector("#final-interpretation-content")
  const engineStatus = document.querySelector("#v6-engine-status")
  const begin = document.querySelector("#begin-examination")

  if (!stage || !form) return

  const qNumEl     = document.querySelector("#v6x-q-num")
  const progressFill = document.querySelector("#v6x-progress-fill")
  const introPane  = document.querySelector("#v6x-intro")
  const questionWrap = document.querySelector("#v6x-question-wrap")

  function renderQuestion(direction = 1) {
    const question = questions[index]
    const numStr   = String(index + 1).padStart(2, "0")
    const total    = String(questions.length).padStart(2, "0")

    if (progressFill) progressFill.style.width = ((index + 1) / questions.length * 100) + "%"

    const isFilled = (answers[question.id] || "").length > 0
    const btnLabel = index === questions.length - 1 ? "SUBMIT" : "CONTINUE"

    // Build the new slide element
    const slide = document.createElement("div")
    slide.className = "v6x-slide"
    slide.style.transform = `translateX(${direction * 100}%)`
    slide.innerHTML = `
      <div class="v6x-q-index">${numStr} / ${total}</div>
      <h2 class="v6x-q-title">${question.label}</h2>
      <p class="v6x-q-prompt">${question.prompt}</p>
      <textarea
        class="v6x-q-textarea"
        name="${question.id}"
        rows="4"
        autocomplete="off"
        spellcheck="false"
      >${answers[question.id] || ""}</textarea>
      <div class="v6x-slide-footer">
        <span class="v6x-counter">${numStr} / ${total}</span>
        <button class="v6x-continue-btn" type="submit"${isFilled ? "" : " disabled"}>${btnLabel}</button>
      </div>
    `

    const oldSlide = stage.querySelector(".v6x-slide.is-active")
    stage.appendChild(slide)

    // Double rAF so starting transform is painted before the transition fires
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        slide.style.transform = "translateX(0%)"
        slide.classList.add("is-active")
        if (oldSlide) {
          oldSlide.classList.remove("is-active")
          oldSlide.style.transform = `translateX(${direction * -100}%)`
          oldSlide.addEventListener("transitionend", () => oldSlide.remove(), { once: true })
        }
      })
    })

    const ta  = slide.querySelector(".v6x-q-textarea")
    const btn = slide.querySelector(".v6x-continue-btn")
    if (ta && btn) {
      ta.addEventListener("input", () => {
        btn.disabled = ta.value.trim().length === 0
      })
    }
    window.setTimeout(() => ta?.focus(), 720)
  }

  let journeyMap = null

  function _showExamQuestionPane() {
    if (introPane)    introPane.style.display    = "none"
    if (questionWrap) questionWrap.style.display = "flex"
    // Activate noise/grain effects now that questions are showing
    examination?.classList.add('is-questioning')
    // Init the journey map only when the user actually enters the questions flow
    if (!journeyMap) {
      const jCanvas = document.querySelector('#v6x-journey-canvas')
      if (jCanvas) {
        journeyMap = initV6xJourney(jCanvas)
        journeyMap?.start()
      }
    }
    renderQuestion()
  }

  function enterExamination() {
    // Move examination to body so its z-index is in the global stacking context,
    // not scoped inside #experience's isolation:isolate stacking context.
    if (examination.parentElement !== document.body) {
      document.body.appendChild(examination)
    }
    examination.hidden = false
    document.body.style.overflow = "hidden"
    // Hide everything else on the page
    document.querySelector('.masthead')?.style.setProperty('display', 'none')
    document.querySelector('.rail')?.style.setProperty('display', 'none')
    document.querySelector('#world')?.style.setProperty('pointer-events', 'none')
    document.querySelector('#experience')?.style.setProperty('visibility', 'hidden')
  }

  // "BEGIN" inside the overlay starts the questions
  document.querySelector("#begin-examination-inner")?.addEventListener("click", _showExamQuestionPane)

  // external "BEGIN THE EXAMINATION" button (from p47-13 scene)
  begin?.addEventListener("click", enterExamination)

  /* ── AUTO-RUN ON PAYMENT HANDOFF ─────────────────────────────────────
   * Stripe returns with ?v6_handoff=...&v6_session=...
   * The payment Worker is authoritative for payment + interpretation.
   * ─────────────────────────────────────────────────────────────────────*/

  ;(async function _checkStripeReturn() {
    const params = new URLSearchParams(window.location.search)
    const handoffToken = params.get("v6_handoff")
    if (!handoffToken) return

    const handoffURL =
      "https://contrarian-layer-v6-payments.kellybhorak.workers.dev/v1/handoff?token=" +
      encodeURIComponent(handoffToken)

    try {
      let payload = null

      for (let attempt = 0; attempt < 30; attempt++) {
        const response = await fetch(handoffURL, { cache: "no-store" })
        if (!response.ok) throw new Error("Payment handoff failed")

        payload = await response.json()

        if (payload.status === "COMPLETE") break
        if (payload.status === "PROCESSING") {
          await new Promise(resolve => setTimeout(resolve, 1000))
          continue
        }

        throw new Error("Payment handoff unavailable")
      }

      if (!payload || payload.status !== "COMPLETE") {
        throw new Error("Interpretation is still processing")
      }

      window.history.replaceState({}, "", window.location.pathname)

      document.querySelectorAll("#narrative, .masthead, .rail").forEach(el => {
        el.style.display = "none"
      })

      document.body.style.overflow = "auto"
      engine.hidden = false

      const result = payload.output
      const serverAnswers = result?.answers || result?.input || {}
      Object.assign(answers, serverAnswers)

      engineStatus.textContent = "INTERPRETATION COMPLETE."
      renderOutput(result)
      output.hidden = false
      final.hidden = false

      output.scrollIntoView({ behavior: "instant", block: "start" })
    } catch (error) {
      console.error("[V6 PAYMENT HANDOFF]", error)
    }
  })()

  form.addEventListener("submit", async event => {
    event.preventDefault()

    const textarea = stage.querySelector(".v6x-slide.is-active .v6x-q-textarea")
    if (!textarea || !textarea.value.trim()) { textarea?.focus(); return }

    const answeredIndex = index
    answers[questions[index].id] = textarea.value.trim()

    // Plot this answer on the journey map + run brick transition
    journeyMap?.plotNode(answeredIndex)
    examination?.classList.add('is-questioning')

    const advanceAfterTransition = () => {
      if (answeredIndex < questions.length - 1) {
        index += 1
        renderQuestion(1)
      } else {
        /* ── STRIPE PAYMENT GATE ─────────────────────────────────────
         * All 12 questions answered. Save and show payment gate.
         * ───────────────────────────────────────────────────────────*/
        const pendingAnswers = JSON.stringify(answers)
        sessionStorage.setItem("cl_pending_answers", pendingAnswers)
        localStorage.setItem("cl_pending_answers", pendingAnswers)

        // Payment threshold becomes the sole visual surface.
        // Hide the entire examination environment, not only the question stage.
        examination?.classList.remove("is-questioning")
        examination?.style.setProperty("visibility", "hidden")
        examination?.style.setProperty("opacity", "0")
        examination?.style.setProperty("pointer-events", "none")
        examination?.style.setProperty("background", "#000")
        examination?.style.setProperty("background-image", "none")

        _runInterpretation(answers)
      }
    }

    if (journeyMap) {
      // Brick settles, then advance
      journeyMap.brickTransition(answeredIndex, advanceAfterTransition)
    } else {
      advanceAfterTransition()
    }
  })

  function _showStripeGate(onSuccess) {
    // Admin bypass: ?v6admin=1 in URL skips payment gate entirely.
    if (new URLSearchParams(window.location.search).get("v6admin") === "1") {
      onSuccess()
      return
    }

    // Never allow two payment thresholds to coexist.
    const existingGate = document.getElementById("cl-stripe-gate")
    if (existingGate) existingGate.remove()

    const PAYMENT_URL =
      "https://buy.stripe.com/7sYeVcfKscly6uZ7mw3cc04"

    const cursorGlow = document.querySelector(".cl-cursor-glow")

    if (cursorGlow) {
      cursorGlow.style.opacity = "0"
      cursorGlow.style.pointerEvents = "none"
    }

    document.body.style.cursor = "default"

    const gate = document.createElement("div")

    gate.id = "cl-stripe-gate"
    gate.setAttribute("role", "dialog")
    gate.setAttribute("aria-modal", "true")
    gate.setAttribute("aria-label", "Interpretation access")

    gate.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      width: 100vw;
      height: 100dvh;
      min-height: 100vh;
      box-sizing: border-box;
      overflow: auto;
      overscroll-behavior: contain;
      isolation: isolate;
      display: flex;
      align-items: center;
      justify-content: center;
      padding:
        max(28px, env(safe-area-inset-top))
        max(24px, env(safe-area-inset-right))
        max(28px, env(safe-area-inset-bottom))
        max(24px, env(safe-area-inset-left));
      background:
        radial-gradient(
          ellipse 75% 60% at 50% 42%,
          rgba(42,48,70,.34) 0%,
          rgba(8,10,18,.78) 48%,
          rgba(3,4,8,.98) 100%
        );
      color: #edf0fa;
      font-family: Manrope, sans-serif;
      text-align: center;
      cursor: default;
      pointer-events: auto;
      opacity: 0;
      transition: opacity 700ms cubic-bezier(.22,1,.36,1);
      -webkit-font-smoothing: antialiased;
    `

    gate.innerHTML = `
      <div
        aria-hidden="true"
        style="
          position:absolute;
          inset:0;
          z-index:-2;
          pointer-events:none;
          background:
            radial-gradient(
              circle at 50% 48%,
              rgba(200,169,110,.055),
              transparent 30%
            ),
            linear-gradient(
              110deg,
              transparent 0%,
              rgba(255,255,255,.018) 48%,
              transparent 56%
            );
        "
      ></div>

      <div
        aria-hidden="true"
        style="
          position:absolute;
          left:8vw;
          right:8vw;
          top:50%;
          height:1px;
          transform:translateY(-50%);
          background:linear-gradient(
            90deg,
            transparent,
            rgba(200,169,110,.18) 20%,
            rgba(220,225,242,.08) 50%,
            rgba(200,169,110,.18) 80%,
            transparent
          );
          pointer-events:none;
        "
      ></div>

      <div
        style="
          position:relative;
          width:min(100%, 760px);
          max-height:100%;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          box-sizing:border-box;
          padding:clamp(18px,4vh,46px) 0;
        "
      >
        <div
          style="
            width:min(100%, 640px);
            display:flex;
            align-items:center;
            justify-content:center;
            gap:18px;
            margin-bottom:clamp(24px,5vh,44px);
          "
        >
          <span
            style="
              width:64px;
              height:1px;
              background:rgba(200,169,110,.42);
            "
          ></span>

          <span
            style="
              font-size:9px;
              line-height:1;
              letter-spacing:.34em;
              text-transform:uppercase;
              color:rgba(200,169,110,.76);
              white-space:nowrap;
            "
          >
            THE CONTRARIAN LAYER
          </span>

          <span
            style="
              width:64px;
              height:1px;
              background:rgba(200,169,110,.42);
            "
          ></span>
        </div>

        <div
          style="
            font-size:10px;
            line-height:1;
            letter-spacing:.32em;
            text-transform:uppercase;
            color:rgba(220,225,242,.42);
            margin-bottom:20px;
          "
        >
          VI · INTERPRETATION
        </div>

        <h2
          style="
            margin:0;
            max-width:700px;
            font-size:clamp(42px,7vw,76px);
            line-height:.94;
            font-weight:400;
            letter-spacing:-.055em;
            color:#f1f3fa;
          "
        >
          Your reading<br>
          is ready.
        </h2>

        <div
          style="
            width:min(100%, 520px);
            height:1px;
            margin:clamp(24px,4vh,36px) 0;
            background:linear-gradient(
              90deg,
              transparent,
              rgba(220,225,242,.15),
              transparent
            );
          "
        ></div>

        <p
          style="
            margin:0 auto;
            width:min(100%, 470px);
            font-size:clamp(14px,1.35vw,16px);
            line-height:1.8;
            font-weight:400;
            color:rgba(220,225,242,.58);
          "
        >
          The twelve answers you provided have been examined against
          the changing structure of the economy. Your interpretation
          is waiting beyond this threshold.
        </p>

        <div
          style="
            position:relative;
            width:min(100%, 430px);
            margin-top:clamp(28px,5vh,48px);
            padding:1px;
            box-sizing:border-box;
            background:linear-gradient(
              135deg,
              rgba(200,169,110,.34),
              rgba(220,225,242,.08) 42%,
              rgba(200,169,110,.20)
            );
            box-shadow:
              0 30px 90px rgba(0,0,0,.48),
              0 0 70px rgba(200,169,110,.035);
            overflow:visible;
          "
        >
          <div
            style="
              position:relative;
              box-sizing:border-box;
              width:100%;
              padding:clamp(24px,4vw,34px);
              background:
                linear-gradient(
                  145deg,
                  rgba(18,21,31,.98),
                  rgba(8,10,17,.98)
                );
              overflow:visible;
            "
          >
            <div
              style="
                font-size:9px;
                line-height:1;
                letter-spacing:.28em;
                text-transform:uppercase;
                color:rgba(220,225,242,.36);
                margin-bottom:22px;
              "
            >
              SECURE ACCESS
            </div>

            <div
              style="
                display:flex;
                align-items:baseline;
                justify-content:center;
                gap:9px;
                margin-bottom:26px;
              "
            >
              <span
                style="
                  font-size:12px;
                  letter-spacing:.12em;
                  text-transform:uppercase;
                  color:rgba(220,225,242,.46);
                "
              >
                One-time access
              </span>

              <span
                style="
                  font-size:25px;
                  font-weight:400;
                  letter-spacing:-.03em;
                  color:rgba(200,169,110,.94);
                "
              >
                $9.99
              </span>
            </div>

            <a
              id="cl-stripe-payment-link"
              href="${PAYMENT_URL}"
              style="
                display:flex;
                align-items:center;
                justify-content:center;
                width:100%;
                min-height:58px;
                box-sizing:border-box;
                border:1px solid rgba(215,190,125,.7);
                background:
                  linear-gradient(
                    180deg,
                    rgba(215,190,125,.12),
                    rgba(215,190,125,.045)
                  );
                color:#f5f0e6;
                text-decoration:none;
                font-family:Arial,Helvetica,sans-serif;
                font-size:11px;
                font-weight:600;
                letter-spacing:.22em;
                text-transform:uppercase;
                cursor:pointer;
                transition:
                  background 220ms ease,
                  border-color 220ms ease,
                  transform 220ms ease;
              "
            >
              Enter the interpretation
            </a>

            <div
              style="
                margin-top:18px;
                font-family:Arial,Helvetica,sans-serif;
                font-size:9px;
                line-height:1.6;
                letter-spacing:.19em;
                text-transform:uppercase;
                color:rgba(242,238,228,.36);
              "
            >
              One-time access · Secure checkout
            </div>
          </div>
        </div>

        <div
          style="
            margin-top:clamp(20px,3vh,30px);
            font-size:10px;
            line-height:1.7;
            letter-spacing:.08em;
            color:rgba(220,225,242,.22);
          "
        >
          Your responses remain part of this interpretation session.
        </div>
      </div>
    `

    const paymentLink = gate.querySelector("#cl-stripe-payment-link")

    if (!paymentLink) {
      throw new Error(
        "Contrarian Layer payment link failed to initialize"
      )
    }

    paymentLink.addEventListener("click", () => {
      if (cursorGlow) {
        cursorGlow.style.opacity = "0"
        cursorGlow.style.pointerEvents = "none"
      }

      document.body.style.cursor = "default"
    })

    document.body.appendChild(gate)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        gate.style.opacity = "1"
      })
    })
  }

  async function _runInterpretation(answersToUse, options = {}) {
    const stripeReturn = options.stripeReturn === true

    const gate = document.getElementById("cl-stripe-gate")
    if (gate) {
      gate.style.opacity = "0"
      window.setTimeout(() => gate.remove(), 900)
    }

    // Restore the cinematic cursor only after the payment threshold
    // has been dismissed.
    if (cursorGlow) {
      cursorGlow.style.opacity = ""
      cursorGlow.style.pointerEvents = "none"
    }
    document.body.style.cursor = ""

    // Normal questionnaire flow closes the examination overlay.
    // A Stripe return is already inside that presentation surface, so keep it open.
    if (!stripeReturn) {
      if (examination) {
        examination.style.opacity = "0"
        examination.style.transition = "opacity 600ms ease"
        window.setTimeout(() => {
          examination.hidden = true
          examination.style.opacity = ""
          examination.style.transition = ""
        }, 650)
      }

      document.body.style.overflow = ""
      document.querySelector('.masthead')?.style.removeProperty('display')
      document.querySelector('.rail')?.style.removeProperty('display')
      document.querySelector('#world')?.style.removeProperty('pointer-events')
      document.querySelector('#experience')?.style.removeProperty('visibility')
    }

    journeyMap?.stop()

    engine.hidden = false

    if (!stripeReturn) {
      engine.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    engineStatus.textContent =
      "Submitting the completed examination to the authoritative V6 boundary."

    try {
      const boundary = await import("/src/engine/v6-boundary.js")
      const payload = await boundary.requestV6Interpretation(
        answersToUse,
        "browser-intake"
      )

      engineStatus.textContent = "INTERPRETATION COMPLETE."

      window.location.href =
        "/interpretation?v6_handoff=" + encodeURIComponent(payload.handoffToken)
      return
    } catch (error) {
      engineStatus.textContent =
        "V6 transport unavailable. No local interpretation was generated."

      output.hidden = false
      outputContent.innerHTML = `
        <div class="v6-error">
          <strong>V6 ENGINE UNAVAILABLE</strong>
          <p>${escapeHtml(error?.message || String(error))}</p>
        </div>
      `
      output.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  function esc(s) {
    const d = document.createElement("div")
    d.textContent = s
    return d.innerHTML
  }

  function renderOutput(payload) {
    outputContent.innerHTML = ""
    finalContent.innerHTML = ""

    const interp = interpret(payload, answers)
    const {
      position,
      positionReading,
      aiExposure,
      defense,
      strategicQuestions,
      direction,
      claudeActions,
      overall,
      hash,
      hasClaude
    } = interp

    const posLabel = POSITION_LABELS[position] || "INTERPRETATION"
    const posColor = POSITION_COLORS[position] || "rgba(185,212,255,.55)"

    let html = `<div class="interp-wrap">`

    /* ── Status bar ── */
    html += `
      <div class="interp-status" style="--pos-color:${posColor}">
        <span class="interp-status-label">${esc(posLabel)}</span>
        ${hasClaude ? `<span class="interp-marker interp-marker--claude">AI-INTERPRETED</span>` : ""}
      </div>`

    /* ── I. What is actually changing ── */
    html += `
      <div class="interp-section">
        <div class="interp-section-header">
          <span class="interp-section-num">I</span>
          <h3 class="interp-section-title">WHAT IS ACTUALLY CHANGING</h3>
        </div>
        <p class="interp-body">${esc(positionReading)}</p>
      </div>`

    /* ── II. Compression and overestimation ── */
    if (aiExposure.length > 0) {
      html += `
        <div class="interp-section">
          <div class="interp-section-header">
            <span class="interp-section-num">II</span>
            <h3 class="interp-section-title">WHAT IS COMPRESSING — AND WHERE YOUR ASSUMPTIONS MAY NEED UPDATING</h3>
          </div>
          ${aiExposure.map(row => `
            <div class="interp-evidence ${row.highlight ? "interp-evidence--highlight" : ""} ${row.warning ? "interp-evidence--warning" : ""}">
              <div class="interp-evidence-meta">
                <span class="interp-evidence-source interp-evidence-source--${esc(row.source === "your answer" ? "self" : "engine")}">${row.source === "your answer" ? "YOUR WORDS" : "READING"}</span>
                <span class="interp-evidence-label">${esc(row.label)}</span>
              </div>
              <p class="interp-evidence-text">${esc(row.text)}</p>
            </div>`).join("")}
        </div>`
    }

    /* ── III. Your signal in the new economy ── */
    if (defense.length > 0) {
      html += `
        <div class="interp-section">
          <div class="interp-section-header">
            <span class="interp-section-num">III</span>
            <h3 class="interp-section-title">YOUR SIGNAL IN THE NEW ECONOMY</h3>
          </div>
          ${defense.map(row => `
            <div class="interp-evidence ${row.highlight ? "interp-evidence--highlight" : ""} ${row.warning ? "interp-evidence--warning" : ""}">
              <div class="interp-evidence-meta">
                <span class="interp-evidence-source interp-evidence-source--${esc(row.source === "your answer" ? "self" : "engine")}">${row.source === "your answer" ? "YOUR WORDS" : "READING"}</span>
                <span class="interp-evidence-label">${esc(row.label)}</span>
              </div>
              <p class="interp-evidence-text">${esc(row.text)}</p>
            </div>`).join("")}
        </div>`
    }

    /* ── IV. Questions the examination surfaces ── */
    if (strategicQuestions.length > 0) {
      html += `
        <div class="interp-section">
          <div class="interp-section-header">
            <span class="interp-section-num">IV</span>
            <h3 class="interp-section-title">QUESTIONS WORTH SITTING WITH</h3>
          </div>
          <p class="interp-body interp-body--muted">The new economy rewards the people who ask the right questions about their own position — and act on what they find. These are the questions your examination surfaced.</p>
          <ol class="interp-questions">
            ${strategicQuestions.map(q => `<li>${esc(q.question)}</li>`).join("")}
          </ol>
        </div>`
    }

    /* ── V. Direction ── */
    html += `
      <div class="interp-section interp-section--direction">
        <div class="interp-section-header">
          <span class="interp-section-num">V</span>
          <h3 class="interp-section-title">WHERE THIS POINTS</h3>
        </div>
        <p class="interp-direction-headline">${esc(direction.headline)}</p>
        <p class="interp-body">${esc(direction.body)}</p>
      </div>`

    /* ── VI. First moves (Claude only) ── */
    if (claudeActions && claudeActions.length > 0) {
      html += `
        <div class="interp-section">
          <div class="interp-section-header">
            <span class="interp-section-num">VI</span>
            <h3 class="interp-section-title">FIRST MOVES</h3>
          </div>
          <p class="interp-body interp-body--muted">Not a prescription — a starting orientation toward the new economy's actual value categories.</p>
          <ol class="interp-questions">
            ${claudeActions.map(a => `<li>${esc(a)}</li>`).join("")}
          </ol>
        </div>`
    }

    html += `</div>`
    outputContent.innerHTML = html

    /* ── Final section ── */
    finalContent.innerHTML = `
      <div class="interp-final">
        <p class="interp-final-directive">${esc(direction.headline)}</p>
        <p class="interp-final-note">The new economy is not taking your job. It is changing what it pays for. The people who navigate that well are the ones who saw it clearly — and moved toward what was opening, not away from what was closing.</p>
      </div>`

    renderTopology(interp)
  }

  function renderTopology(interp) {
    const wrap = document.querySelector("#topology-canvas")
    if (!wrap) return

    const { position, markers, defense, aiExposure, answers, overall } = interp
    const COLORS = {
      DEFENDED: 0x78dca0,
      EXPOSED: 0xff8c64,
      PARTIAL: 0xb9d4ff,
      UNCLEAR: 0xf2c864,
      UNCERTAIN: 0x8899aa
    }
    const posColor = COLORS[position] || 0xb9d4ff

    wrap.innerHTML = ""
    const canvas = document.createElement("canvas")
    wrap.appendChild(canvas)

    const w = wrap.clientWidth || 600
    const h = Math.min(420, window.innerHeight * 0.55)
    canvas.width = w
    canvas.height = h

    const topoRenderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
    topoRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    topoRenderer.setSize(w, h)
    topoRenderer.setClearColor(0x000000, 0)

    const topoScene = new THREE.Scene()
    const topoCamera = new THREE.PerspectiveCamera(52, w / h, 0.1, 100)
    topoCamera.position.set(0, 0, 14)

    const topoAmbient = new THREE.AmbientLight(0xffffff, 0.6)
    topoScene.add(topoAmbient)
    const topoKey = new THREE.PointLight(0xaec8ff, 30, 30)
    topoKey.position.set(5, 5, 8)
    topoScene.add(topoKey)

    const nodes = []
    const group = new THREE.Group()
    topoScene.add(group)

    function makeNode(x, y, z, radius, color, opacity) {
      const geo = new THREE.IcosahedronGeometry(radius, 2)
      const mat = new THREE.MeshPhysicalMaterial({
        color,
        transparent: true,
        opacity,
        roughness: 0.2,
        metalness: 0.5,
        wireframe: false
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.position.set(x, y, z)
      group.add(mesh)

      const wireMat = new THREE.MeshBasicMaterial({ color, wireframe: true, transparent: true, opacity: opacity * 0.35 })
      const wireMesh = new THREE.Mesh(geo, wireMat)
      wireMesh.position.set(x, y, z)
      group.add(wireMesh)

      nodes.push({ mesh, wireMesh, x, y, z })
      return mesh
    }

    function makeLine(a, b, color) {
      const points = [new THREE.Vector3(a.x, a.y, a.z), new THREE.Vector3(b.x, b.y, b.z)]
      const geo = new THREE.BufferGeometry().setFromPoints(points)
      const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.28 })
      group.add(new THREE.Line(geo, mat))
    }

    const centerNode = { x: 0, y: 0, z: 0 }
    makeNode(0, 0, 0, 1.05, posColor, 0.82)

    const defenseNodes = defense.filter(d => !d.warning).slice(0, 4)
    const aiNodes = aiExposure.filter(r => r.source === "engine").slice(0, 3)

    const defenseColor = 0x78dca0
    const aiColor = 0xff8c64
    const desiredColor = 0xe0c8ff

    defenseNodes.forEach((d, i) => {
      const angle = (Math.PI * 2 / Math.max(defenseNodes.length, 1)) * i + Math.PI * 0.22
      const r = 3.8
      const nx = Math.cos(angle) * r
      const ny = Math.sin(angle) * r * 0.5
      const nz = Math.sin(angle) * r * 0.4
      const n = { x: nx, y: ny, z: nz }
      makeNode(nx, ny, nz, 0.52, defenseColor, 0.65)
      makeLine(centerNode, n, defenseColor)
    })

    aiNodes.forEach((r, i) => {
      const angle = (Math.PI * 2 / Math.max(aiNodes.length, 1)) * i + Math.PI * 1.18
      const rad = 3.2
      const nx = Math.cos(angle) * rad
      const ny = Math.sin(angle) * rad * 0.5 - 0.6
      const nz = Math.sin(angle) * rad * 0.3
      const n = { x: nx, y: ny, z: nz }
      makeNode(nx, ny, nz, 0.44, aiColor, 0.55)
      makeLine(centerNode, n, aiColor)
    })

    if (answers?.desired?.length > 8) {
      const dn = { x: 0.4, y: 3.5, z: 0.8 }
      makeNode(dn.x, dn.y, dn.z, 0.68, desiredColor, 0.5)
      makeLine(centerNode, dn, desiredColor)
    }

    const dustGeo = new THREE.BufferGeometry()
    const dustPos = new Float32Array(240 * 3)
    for (let i = 0; i < 240; i++) {
      dustPos[i * 3] = (Math.random() - 0.5) * 22
      dustPos[i * 3 + 1] = (Math.random() - 0.5) * 14
      dustPos[i * 3 + 2] = (Math.random() - 0.5) * 10
    }
    dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPos, 3))
    const dustMat = new THREE.PointsMaterial({ color: 0x8899cc, size: 0.045, transparent: true, opacity: 0.38 })
    group.add(new THREE.Points(dustGeo, dustMat))

    let topoFrame = 0
    let topoActive = true

    function animateTopo() {
      if (!topoActive) return
      requestAnimationFrame(animateTopo)
      group.rotation.y += 0.0028
      group.rotation.x = Math.sin(topoFrame * 0.0009) * 0.12
      topoFrame++
      topoRenderer.render(topoScene, topoCamera)
    }

    animateTopo()

    const obs = new IntersectionObserver(entries => {
      topoActive = entries[0].isIntersecting
      if (topoActive) animateTopo()
    }, { threshold: 0.1 })
    obs.observe(wrap)
  }

  function escapeHtml(value) {
    const div = document.createElement("div")
    div.textContent = value
    return div.innerHTML
  }

  renderQuestion()
})()

/* ── EXAMINATION NOISE CANVAS — domain-warp GLSL ───────────────────
 * Runs only when the examination overlay is open.
 * CineShader-style procedural noise rendered to a 2D canvas.
 * ───────────────────────────────────────────────────────────────── */
;(function _initExamNoise() {
  const noiseCanvas = document.querySelector("#v6x-noise-canvas")
  if (!noiseCanvas) return

  const gl = noiseCanvas.getContext("webgl") || noiseCanvas.getContext("experimental-webgl")
  if (!gl) return

  const vert = `
    attribute vec2 a_pos;
    void main(){ gl_Position = vec4(a_pos, 0., 1.); }
  `
  const frag = `
    precision mediump float;
    uniform float u_time;
    uniform vec2  u_res;

    vec3 mod289(vec3 x){ return x - floor(x * (1./289.)) * 289.; }
    vec2 mod289(vec2 x){ return x - floor(x * (1./289.)) * 289.; }
    vec3 permute(vec3 x){ return mod289(((x*34.)+1.)*x); }

    float snoise(vec2 v){
      const vec4 C = vec4(.211324865405187,.366025403784439,-.577350269189626,.024390243902439);
      vec2 i  = floor(v + dot(v, C.yy));
      vec2 x0 = v - i + dot(i, C.xx);
      vec2 i1 = (x0.x > x0.y) ? vec2(1.,0.) : vec2(0.,1.);
      vec4 x12 = x0.xyxy + C.xxzz;
      x12.xy -= i1;
      i = mod289(i);
      vec3 p  = permute(permute(i.y + vec3(0., i1.y, 1.)) + i.x + vec3(0., i1.x, 1.));
      vec3 m  = max(.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.);
      m = m*m*m*m*2.;
      vec3 x  = 2.*fract(p * C.www) - 1.;
      vec3 h  = abs(x) - .5;
      vec3 ox = floor(x + .5);
      vec3 a0 = x - ox;
      m *= 1.79284291400159 - .85373472095314 * (a0*a0 + h*h);
      vec3 g;
      g.x  = a0.x  * x0.x   + h.x  * x0.y;
      g.yz = a0.yz * x12.xz + h.yz * x12.yw;
      return 130. * dot(m, g);
    }

    void main(){
      vec2 uv = gl_FragCoord.xy / u_res;
      float t  = u_time * 0.18;

      // domain warp — two layers of noise distort the sample coordinates
      vec2 p = uv * 2.2 - 1.1;
      vec2 q = vec2(snoise(p + t), snoise(p + 1.7));
      vec2 r = vec2(snoise(p + 4.*q + vec2(1.7, 9.2) + .15*t),
                    snoise(p + 4.*q + vec2(8.3, 2.8) + .126*t));

      float n = snoise(p + 4.*r);
      n = n * .5 + .5;

      // Blue-tinted: deep navy → electric blue → violet edge
      vec3 c0  = vec3(0.01, 0.03, 0.18);
      vec3 c1  = vec3(0.04, 0.14, 0.48);
      vec3 c2  = vec3(0.22, 0.08, 0.55);
      float band = smoothstep(0.15, 0.55, n);
      float edge = smoothstep(0.62, 0.98, n);
      vec3 col = mix(mix(c0, c1, band), c2, edge);
      col = col * (0.5 + n * 2.0);

      gl_FragColor = vec4(col, 1.);
    }
  `

  function _compileShader(type, src) {
    const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s
  }
  const prog = gl.createProgram()
  gl.attachShader(prog, _compileShader(gl.VERTEX_SHADER, vert))
  gl.attachShader(prog, _compileShader(gl.FRAGMENT_SHADER, frag))
  gl.linkProgram(prog)
  gl.useProgram(prog)

  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW)
  const aPos = gl.getAttribLocation(prog, "a_pos")
  gl.enableVertexAttribArray(aPos)
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)

  const uTime = gl.getUniformLocation(prog, "u_time")
  const uRes  = gl.getUniformLocation(prog, "u_res")

  let _noiseRAF = null
  function _resize() {
    noiseCanvas.width  = Math.floor(noiseCanvas.clientWidth  * Math.min(window.devicePixelRatio, 1.5))
    noiseCanvas.height = Math.floor(noiseCanvas.clientHeight * Math.min(window.devicePixelRatio, 1.5))
    gl.viewport(0, 0, noiseCanvas.width, noiseCanvas.height)
  }

  function _tick(t) {
    const exam = document.querySelector("#v6-examination")
    if (!exam || exam.hidden) { _noiseRAF = null; return }
    gl.uniform1f(uTime, t * 0.001)
    gl.uniform2f(uRes, noiseCanvas.width, noiseCanvas.height)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    _noiseRAF = requestAnimationFrame(_tick)
  }

  // start/stop based on overlay visibility using MutationObserver
  const exam = document.querySelector("#v6-examination")
  if (exam) {
    new MutationObserver(() => {
      if (!exam.hidden && !_noiseRAF) { _resize(); _noiseRAF = requestAnimationFrame(_tick) }
    }).observe(exam, { attributes: true, attributeFilter: ["hidden"] })
    window.addEventListener("resize", _resize)
  }
})()

